import Loading from '../../../components/Loading';
import {sessionColumns} from './sessionColumns';
import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
// SesionesPosPage.tsx — Co Marca Admin — estilo AboutusProjects
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Banknote, Eye, FileText, ChevronRight, Home } from 'lucide-react';
import { fetchSesionesPorUsuario } from '../../../services/pos/sesionPOSService';
import { fetchReporteSesionPOS } from '../../../services/pos/sesionPOSService';
import { generarPDFReporteSesionPOS } from '../../../utils/pdfReporteSesionPOS';

const colors = {
    primary:        '#612570',
    primaryDark:    '#4a154b',
    secondary:      'var(--ad-primary, #3564db)',
    secondaryLight: '#059669',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `

.ses-page * { font-family:'Plus Jakarta Sans',sans-serif; box-sizing:border-box; }
@keyframes sesFadeUp { from{opacity:0;transform:translateY(16px);} to{opacity:1;transform:translateY(0);} }
@keyframes spin { to{transform:rotate(360deg);} }
.ses-fadeup   { animation:sesFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ses-fadeup-1 { animation:sesFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }

.ses-table-row { transition:background .12s; }
.ses-table-row:hover { background:#faf5ff; }

.ses-btn-view {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; border:none; cursor:pointer;
    font-size:.78rem; font-weight:700; font-family:'Plus Jakarta Sans',sans-serif;
    background:#fdf2f8; color:var(--ad-link, #2854c5);
    transition:all .2s;
}
.ses-btn-view:hover { background:${colors.secondary}; color:#fff; transform:translateY(-1px); }

.ses-btn-pdf {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; border:none; cursor:pointer;
    font-size:.78rem; font-weight:700; font-family:'Plus Jakarta Sans',sans-serif;
    background:#ecfdf5; color:#065f46;
    transition:all .2s;
}
.ses-btn-pdf:hover:not(:disabled) { background:#059669; color:#fff; transform:translateY(-1px); }
.ses-btn-pdf:disabled { opacity:.5; cursor:not-allowed; }

.ses-btn-back {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-surface, #fff); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:9px 16px; font-size:.85rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.ses-btn-back:hover { background:var(--ad-soft, #f8fafc); border-color:color-mix(in srgb, ${colors.secondary} 25.1%, transparent); color:var(--ad-link, #2854c5); }

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97,37,112,.2); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97,37,112,.4); }
`;

const SesionesPosPage: React.FC = () => {
    const { posId } = useParams<{ posId: string }>();
    const navigate = useNavigate();
    const [sesiones, setSesiones] = useState<any[]>([]);
    const [loading, setLoading]   = useState(true);
    const [generandoPDF, setGenerandoPDF] = useState<number|null>(null);

    const handleGenerarPDF = async (sesionId: number) => {
        setGenerandoPDF(sesionId);
        try {
            const data = await fetchReporteSesionPOS(sesionId);
            await generarPDFReporteSesionPOS(data);
        } catch { alert('Error al generar el reporte. Por favor, intente nuevamente.'); }
        finally { setGenerandoPDF(null); }
    };

    useEffect(() => {
        if (!posId) return;
        fetchSesionesPorUsuario(Number(posId))
            .then(setSesiones)
            .catch((() => undefined))
            .finally(() => setLoading(false));
    }, [posId]);

    const formatDate = (d: string) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleDateString('es-ES', { year:'numeric', month:'short', day:'numeric' });
    };
    const formatTime = (d: string) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleTimeString('es-ES', { hour:'2-digit', minute:'2-digit' });
    };
    const formatSaldo = (v: any) => {
        const n = Number(v);
        return isNaN(n) ? '0.00' : n.toFixed(2);
    };

    const getEstadoStyle = (estado: string) => {
        switch (estado) {
            case 'cerrada': return { bg:`color-mix(in srgb, ${colors.secondaryLight} 7.84%, transparent)`, border:`1px solid color-mix(in srgb, ${colors.secondaryLight} 18.82%, transparent)`, color:'var(--ad-success, #065f46)', dot:colors.secondaryLight };
            case 'abierta': return { bg:`color-mix(in srgb, ${colors.secondary} 7.06%, transparent)`, border:`1px solid color-mix(in srgb, ${colors.secondary} 14.51%, transparent)`, color:'var(--ad-link, #2854c5)', dot:colors.secondary };
            default:        return { bg:`color-mix(in srgb, ${colors.primary} 6.27%, transparent)`, border:`1px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, color:'var(--ad-link, #2854c5)', dot:colors.primary };
        }
    };

    // ── Loading ──
    if (loading) return (
        <Loading text="Cargando información…"/>
    );

    return <LumaResourceWorkspace title="Sesiones del punto de venta" description="Consulta y gestiona la información de tu coworking." group="Punto de venta" rows={sesiones} nameKey="codigo" statusKey="estado" columns={sessionColumns} extraActions={r=><><button type="button" onClick={()=>navigate(`/sesiones-pos/${r.id}`)}>Detalle de sesión</button><button type="button" onClick={()=>navigate(`/sesiones-pos/${r.id}/pagos`)}>Pagos</button><button type="button" disabled={generandoPDF===r.id} onClick={()=>handleGenerarPDF(r.id)}>PDF</button></>}><div className="vx-metric-grid">{[{label:'Sesiones POS',value:sesiones.length,sub:'Sesiones consultadas'},{label:'Recaudación',value:lumaMoney(sesiones.reduce((n,r)=>n+Number(r.total_pagos||0),0)),sub:'Cobros registrados'},{label:'Diferencias',value:lumaMoney(sesiones.filter(r=>r.estado==='cerrada'&&r.saldo_final!==null).reduce((n,r)=>n+Number(r.saldo_final)-Number(r.saldo_actual),0)),sub:'Cierres de caja'}].map(m=><article className="vx-metric" key={m.label}><p>{m.label}</p><strong>{m.value}</strong><small>{m.sub}</small></article>)}</div></LumaResourceWorkspace>;
};
export default SesionesPosPage;
